﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Link , useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  useLoginMutation,
  useResendVerificationMutation,
} from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";

const schema = z.object({
  email: z.string().email("Geçerli bir email gir."),
  password: z.string().min(1, "Şifre gerekli."),
});

type LoginFormValues = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useLoginMutation();
  const resend = useResendVerificationMutation();

  const from = searchParams.get("from") ?? "/";

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  async function handleResend(email: string) {
    try {
      await resend.mutateAsync({ email });
      toast.success("Doğrulama linki yeniden gönderildi. Emailini kontrol et.");
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.detail
          : "Link gönderilemedi, biraz sonra tekrar dene.",
      );
    }
  }

  async function onSubmit(values: LoginFormValues) {
    try {
      await login.mutateAsync(values);
      toast.success("Giriş başarılı!");
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "email_not_verified") {
          toast.error(err.detail, {
            duration: 10000,
            action: {
              label: "Tekrar Gönder",
              onClick: () => handleResend(values.email),
            },
          });
          return;
        }
        toast.error(err.detail);
      } else {
        toast.error("Beklenmeyen bir hata oluştu.");
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/40">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">Giriş Yap</CardTitle>
          <CardDescription>
            Dashboard&apos;a devam etmek için giriş yap.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="sen@ornek.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Şifre</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={login.isPending}>
                {login.isPending ? "Giriş yapılıyor..." : "Giriş Yap"}
              </Button>
            </form>
          </Form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Henüz hesabın yok mu?{" "}
            <Link to="/signup" className="font-medium text-foreground hover:underline">
              Hesap oluştur
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
